import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Bot, User, Clock, Send, MessageSquare, FileText, StickyNote, Shield, Timer, Tag, X, Wifi } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/atendimento`;
const headers = () => ({Authorization: `Bearer ${localStorage.getItem('recupnet_token')}`});

const STATUS_MAP = {
  open:{label:'Aberto',badge:'badge-neutral'}, waiting_ai:{label:'IA',badge:'badge-success'},
  waiting_human:{label:'Fila Humano',badge:'badge-warning'}, in_progress:{label:'Em Atendimento',badge:'badge-success'},
  on_hold:{label:'Em Espera',badge:'badge-neutral'}, closed:{label:'Encerrado',badge:'badge-neutral'},
};

export default function AtendimentoTicket() {
  const { convId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [notes, setNotes] = useState([]);
  const [sla, setSla] = useState(null);
  const [knowledge, setKnowledge] = useState([]);
  const [macros, setMacros] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMsg, setNewMsg] = useState('');
  const [newNote, setNewNote] = useState('');
  const [tab, setTab] = useState('chat');
  const [sending, setSending] = useState(false);
  const [syncStatus, setSyncStatus] = useState('online');
  const pollRef = useRef(null);
  const msgsEndRef = useRef(null);

  const fetchAll = useCallback(async (silent = false) => {
    try {
      const [convR, tlR, notesR, slaR] = await Promise.all([
        axios.get(`${API}/conversations/${convId}`, {headers: headers()}),
        axios.get(`${API}/conversations/${convId}/timeline`, {headers: headers()}),
        axios.get(`${API}/conversations/${convId}/notes`, {headers: headers()}),
        axios.get(`${API}/conversations/${convId}/sla-status`, {headers: headers()}).catch(() => ({data:{}})),
      ]);
      setDetail(prev => {
        const newMsgCount = convR.data.messages?.length || 0;
        const oldMsgCount = prev?.messages?.length || 0;
        if (newMsgCount !== oldMsgCount || !prev) return convR.data;
        return { ...prev, conversation: convR.data.conversation, contact: convR.data.contact, recupLink: convR.data.recupLink };
      });
      setTimeline(tlR.data);
      setNotes(notesR.data);
      setSla(slaR.data);
      setSyncStatus('online');
      
      // Load static data only once
      if (!silent) {
        const [kbR, macR, auR, attR] = await Promise.all([
          axios.get(`${API}/knowledge?search=`, {headers: headers()}),
          axios.get(`${API}/macros`, {headers: headers()}).catch(() => ({data:[]})),
          axios.get(`${API}/conversations/${convId}/audit`, {headers: headers()}).catch(() => ({data:[]})),
          axios.get(`${API}/conversations/${convId}/attachments`, {headers: headers()}).catch(() => ({data:[]})),
        ]);
        setKnowledge(Array.isArray(kbR.data) ? kbR.data.slice(0,3) : []);
        setMacros(Array.isArray(macR.data) ? macR.data : []);
        setAuditLogs(Array.isArray(auR.data) ? auR.data : []);
        setAttachments(Array.isArray(attR.data) ? attR.data : []);
      }
    } catch { setSyncStatus('error'); }
    finally { if (!silent) setLoading(false); }
  }, [convId]);

  // Initial load
  useEffect(() => { fetchAll(false); }, [convId]);

  // Polling every 3s for messages
  useEffect(() => {
    pollRef.current = setInterval(() => fetchAll(true), 3000);
    return () => clearInterval(pollRef.current);
  }, [fetchAll]);

  // Smart auto-scroll
  useEffect(() => {
    if (msgsEndRef.current && tab === 'chat') {
      const container = msgsEndRef.current.parentElement;
      if (container) {
        const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 120;
        if (nearBottom) msgsEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [detail?.messages?.length, tab]);

  const sendMessage = async () => {
    if (!newMsg.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/conversations/${convId}/messages`, {content: newMsg, sender: 'operator', senderName: 'Operador'}, {headers: headers()});
      setNewMsg('');
      fetchAll(true);
    } catch {} finally { setSending(false); }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    await axios.post(`${API}/conversations/${convId}/notes`, {content: newNote, authorName: 'Admin'}, {headers: headers()});
    setNewNote('');
    fetchAll();
  };

  const transfer = async (target) => {
    await axios.post(`${API}/conversations/${convId}/transfer`, {target}, {headers: headers()});
    fetchAll();
  };

  const classify = async () => {
    await axios.post(`${API}/conversations/${convId}/classify`, {}, {headers: headers()});
    fetchAll();
  };

  const autoAssign = async () => {
    const r = await axios.post(`${API}/conversations/${convId}/auto-assign`, {}, {headers: headers()});
    if (r.data.assigned) fetchAll();
  };

  if (loading) return <div className="flex justify-center py-20"><div className="rn-spinner" style={{width:28,height:28}}/></div>;

  const conv = detail?.conversation;
  const contact = detail?.contact;
  const st = STATUS_MAP[conv?.status] || STATUS_MAP.open;

  return (
    <div className="flex h-screen" style={{maxHeight:'100vh'}}>
      {/* MAIN CONTENT */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-3 border-b border-white/10 flex items-center gap-3" style={{background:'rgba(15,20,24,0.9)'}}>
          <button onClick={() => navigate('/admin/atendimento/inbox')} className="p-1.5 rounded-lg hover:bg-white/5"><ArrowLeft size={18}/></button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold">{conv?.contactName}</h2>
              <span className={`${st.badge} text-[9px]`}>{st.label}</span>
              {conv?.priority === 'urgent' && <span className="badge-danger text-[8px]">URGENTE</span>}
              {conv?.priority === 'high' && <span className="badge-warning text-[8px]">ALTA</span>}
              {conv?.category && <span className="badge-neutral text-[8px]">{conv.category}</span>}
              {conv?.sentiment === 'negative' && <span className="badge-danger text-[8px]">Insatisfeito</span>}
            </div>
            <p className="text-[10px] text-white/30">{conv?.contactPhone} · {conv?.protocol} · {conv?.currentOwner === 'ai' ? 'IA' : conv?.operatorName || 'Sem operador'}</p>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="flex items-center gap-1 mr-2" title="Atualizacao em tempo real">
              <div className={`w-1.5 h-1.5 rounded-full ${syncStatus === 'online' ? 'bg-[var(--rn-green)]' : 'bg-red-400'}`}/>
              <span className="text-[8px] text-white/20">{syncStatus === 'online' ? 'Live' : 'Off'}</span>
            </div>
            <button onClick={classify} className="text-[9px] px-2.5 py-1 rounded-lg bg-white/5 text-white/50 hover:bg-white/10">Classificar</button>
            <button onClick={autoAssign} className="text-[9px] px-2.5 py-1 rounded-lg bg-white/5 text-white/50 hover:bg-white/10">Auto-atribuir</button>
            {conv?.currentOwner !== 'ai' && <button onClick={() => transfer('ai')} className="text-[9px] px-2.5 py-1 rounded-lg bg-white/5 text-white/50 hover:bg-white/10">IA</button>}
            {conv?.currentOwner !== 'human' && <button onClick={() => transfer('human')} className="text-[9px] px-2.5 py-1 rounded-lg bg-white/5" style={{color:'var(--rn-green)'}}>Assumir</button>}
          </div>
        </div>

        {/* SLA Banner */}
        {sla?.atRisk && (
          <div className="px-3 py-2 flex items-center gap-2" style={{background:'rgba(249,168,37,0.08)'}}>
            <Timer size={14} style={{color:'var(--rn-warning)'}}/><span className="text-xs text-yellow-400">SLA em risco — {Math.round((sla.resolution?.remainingSeconds||0)/60)} min restantes</span>
          </div>
        )}
        {sla?.firstResponse?.breached && (
          <div className="px-3 py-2 flex items-center gap-2" style={{background:'rgba(211,47,47,0.08)'}}>
            <Shield size={14} style={{color:'#D32F2F'}}/><span className="text-xs text-red-400">SLA de primeira resposta estourado</span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 px-3 pt-2 border-b border-white/10">
          {[{k:'chat',l:'Chat'},{k:'timeline',l:'Timeline'},{k:'notes',l:'Notas'},{k:'audit',l:'Auditoria'}].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`px-3 py-1.5 text-xs rounded-t-lg transition-colors ${tab===t.k ? 'bg-white/[0.06] text-white border-b-2 border-[var(--rn-green)]' : 'text-white/40 hover:text-white/60'}`}>
              {t.l}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{background:'rgba(11,15,16,0.4)'}}>
          {tab === 'chat' && detail?.messages?.map(m => (
            <div key={m.id} className={`flex ${m.sender==='client'?'justify-start':'justify-end'}`}>
              <div className={`max-w-[70%] rounded-2xl px-3.5 py-2.5 ${m.type==='system'?'bg-white/5 text-white/30 text-center mx-auto text-[10px] italic max-w-full':m.sender==='client'?'bg-white/[0.08] text-white/85':m.isAI?'bg-[rgba(99,203,182,0.12)] text-white/85':'bg-[rgba(99,203,182,0.2)] text-white/90'}`}>
                {m.type!=='system' && <p className="text-[9px] text-white/35 mb-0.5">{m.isAI?'🤖 ':''}{m.senderName}</p>}
                <p className="text-xs leading-relaxed">{m.content}</p>
                <p className="text-[8px] text-white/20 mt-1">{m.createdAt?new Date(m.createdAt).toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit'}):''}</p>
              </div>
            </div>
          ))}
          {tab === 'chat' && <div ref={msgsEndRef}/>}

          {tab === 'timeline' && timeline.map((ev, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${ev.eventType==='note'?'bg-yellow-500/20':ev.isAI?'bg-[rgba(99,203,182,0.2)]':'bg-white/10'}`}>
                {ev.eventType==='note'?<StickyNote size={11} className="text-yellow-400"/>:ev.isAI?<Bot size={11} style={{color:'var(--rn-green)'}}/>:<User size={11} className="text-white/50"/>}
              </div>
              <div className="flex-1 pb-3">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-medium text-white/60">{ev.senderName || ev.authorName || 'Sistema'}</span>
                  <span className="text-[8px] text-white/20">{ev.createdAt?new Date(ev.createdAt).toLocaleString('pt-BR',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'}):''}</span>
                  {ev.eventType==='note' && <span className="badge-warning text-[7px]">NOTA</span>}
                </div>
                <p className="text-xs text-white/50 mt-0.5">{ev.content}</p>
              </div>
            </div>
          ))}

          {tab === 'audit' && (
            <div className="space-y-2">
              {auditLogs.length === 0 ? <p className="text-xs text-white/25 text-center py-4">Nenhum registro</p> :
              auditLogs.map(log => (
                <div key={log.id} className="flex items-start gap-2 p-2 rounded-lg bg-white/[0.02] border border-white/5">
                  <Shield size={12} className="text-white/20 mt-0.5 flex-shrink-0"/>
                  <div>
                    <p className="text-[10px] text-white/60">{log.action}</p>
                    <p className="text-[9px] text-white/30">{log.actor} · {log.timestamp ? new Date(log.timestamp).toLocaleString('pt-BR',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'}) : ''}</p>
                    {log.details && Object.keys(log.details).length > 0 && <p className="text-[8px] text-white/20">{JSON.stringify(log.details)}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'notes' && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <input value={newNote} onChange={e=>setNewNote(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addNote()}
                  placeholder="Adicionar nota interna..."
                  className="flex-1 bg-black/20 border border-white/10 text-white text-xs placeholder:text-white/25 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-[rgba(99,203,182,0.3)]"/>
                <button onClick={addNote} className="btn-primary !py-2 !px-4 text-xs">Salvar</button>
              </div>
              {notes.map(n => (
                <div key={n.id} className="p-3 rounded-xl bg-yellow-500/[0.05] border border-yellow-500/20">
                  <div className="flex items-center gap-2 mb-1">
                    <StickyNote size={12} className="text-yellow-400"/>
                    <span className="text-[10px] font-medium text-yellow-400/80">{n.authorName}</span>
                    <span className="text-[8px] text-white/20">{n.createdAt?new Date(n.createdAt).toLocaleString('pt-BR',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'}):''}</span>
                  </div>
                  <p className="text-xs text-white/60">{n.content}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Message Input (only on chat tab) */}
        {tab === 'chat' && conv?.status !== 'closed' && (
          <div className="p-3 border-t border-white/10 flex gap-2" style={{background:'rgba(15,20,24,0.8)'}}>
            <input value={newMsg} onChange={e=>setNewMsg(e.target.value)} onKeyDown={e=>e.key==='Enter'&&sendMessage()}
              placeholder="Digite sua mensagem..."
              className="flex-1 bg-black/20 border border-white/10 text-white text-xs placeholder:text-white/25 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-[rgba(99,203,182,0.3)]"/>
            <button onClick={sendMessage} disabled={sending||!newMsg.trim()} className="btn-primary !py-2 !px-4 text-xs disabled:opacity-30"><Send size={14}/></button>
          </div>
        )}
      </div>

      {/* RIGHT PANEL: Client + Context */}
      <div className="w-[280px] border-l border-white/10 overflow-y-auto flex-shrink-0 p-4 space-y-4" style={{background:'rgba(11,15,16,0.6)'}}>
        {/* Contact */}
        <div>
          <h4 className="text-xs font-semibold text-white/60 mb-2">Perfil do Cliente</h4>
          <div className="space-y-1.5 text-xs">
            <div><span className="text-white/30">Nome:</span> <span className="text-white/80">{contact?.name}</span></div>
            <div><span className="text-white/30">Telefone:</span> <span className="text-white/80 font-mono">{contact?.phone}</span></div>
            {contact?.cpf && <div><span className="text-white/30">CPF:</span> <span className="text-white/80 font-mono">{contact.cpf}</span></div>}
            <div><span className="text-white/30">Canal:</span> <span className="text-white/80">{contact?.origin}</span></div>
            <div><span className="text-white/30">Atendimentos:</span> <span className="text-white/80">{contact?.totalConversations}</span></div>
          </div>
          {contact?.tags?.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{contact.tags.map(t => <span key={t} className="badge-neutral text-[8px]">{t}</span>)}</div>}
        </div>

        {/* Conversation Details */}
        <div className="border-t border-white/10 pt-3">
          <h4 className="text-xs font-semibold text-white/60 mb-2">Detalhes</h4>
          <div className="space-y-1 text-[10px] text-white/35">
            <p>Fila: <span className="text-white/60">{conv?.queueName}</span></p>
            <p>Protocolo: <span className="text-white/60 font-mono">{conv?.protocol}</span></p>
            <p>Mensagens: <span className="text-white/60">{conv?.messageCount}</span></p>
            <p>Responsavel: <span className="text-white/60">{conv?.currentOwner==='ai'?'IA':conv?.operatorName||'—'}</span></p>
            {conv?.category && <p>Categoria: <span className="text-white/60">{conv.category}</span></p>}
            {conv?.sentiment && <p>Sentimento: <span className={conv.sentiment==='negative'?'text-red-400':conv.sentiment==='positive'?'text-[var(--rn-green)]':'text-white/60'}>{conv.sentiment}</span></p>}
          </div>
        </div>

        {/* SLA */}
        {sla?.resolution && (
          <div className="border-t border-white/10 pt-3">
            <h4 className="text-xs font-semibold text-white/60 mb-2 flex items-center gap-1"><Timer size={12}/> SLA</h4>
            <div className="space-y-1 text-[10px]">
              <div className="flex items-center justify-between">
                <span className="text-white/35">1a Resposta:</span>
                <span className={sla.firstResponse?.breached?'text-red-400':'text-[var(--rn-green)]'}>{sla.firstResponse?.breached?'Estourado':`${Math.round((sla.firstResponse?.remainingSeconds||0)/60)}min`}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/35">Resolucao:</span>
                <span className={sla.resolution?.breached?'text-red-400':sla.atRisk?'text-yellow-400':'text-[var(--rn-green)]'}>{sla.resolution?.breached?'Estourado':`${Math.round((sla.resolution?.remainingSeconds||0)/60)}min`}</span>
              </div>
            </div>
          </div>
        )}

        {/* Knowledge Suggestions */}
        {knowledge?.length > 0 && (
          <div className="border-t border-white/10 pt-3">
            <h4 className="text-xs font-semibold text-white/60 mb-2">Sugestoes da Base</h4>
            <div className="space-y-1.5">
              {knowledge.map(a => (
                <div key={a.id} className="p-2 rounded-lg bg-white/[0.03] border border-white/5 cursor-pointer hover:border-[rgba(99,203,182,0.15)]"
                  onClick={() => setNewMsg(prev => prev + (prev?' ':'') + (a.content || '').slice(0,100))}>
                  <p className="text-[10px] font-medium text-white/60">{a.title}</p>
                  <p className="text-[9px] text-white/30 line-clamp-2">{(a.content || '').slice(0,80)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Macros (Response Templates) */}
        {macros?.length > 0 && (
          <div className="border-t border-white/10 pt-3">
            <h4 className="text-xs font-semibold text-white/60 mb-2">Respostas Rapidas</h4>
            <div className="space-y-1">
              {macros.slice(0, 5).map(m => (
                <button key={m.id} className="w-full text-left p-2 rounded-lg bg-white/[0.02] border border-white/5 hover:border-[rgba(99,203,182,0.15)] transition-colors"
                  onClick={async () => { setNewMsg(m.content || ''); await axios.post(`${API}/macros/${m.id}/use`, {}, {headers: headers()}).catch(() => {}); }}>
                  <p className="text-[10px] font-medium text-white/50">{m.title}</p>
                  <p className="text-[8px] text-white/25 truncate">{(m.content || '').slice(0,60)}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Attachments */}
        {attachments?.length > 0 && (
          <div className="border-t border-white/10 pt-3">
            <h4 className="text-xs font-semibold text-white/60 mb-2">Anexos ({attachments.length})</h4>
            <div className="space-y-1">
              {attachments.map(a => (
                <div key={a.id} className="flex items-center gap-2 p-1.5 rounded-lg bg-white/[0.02]">
                  <FileText size={12} className="text-white/30"/>
                  <span className="text-[10px] text-white/50 truncate">{a.fileName}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
